import React, { ReactNode } from "react";
import { cn } from "@/shared/utils";
import { Badge } from "../ui/Badge";
import { Accolades } from "./Accolades";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/pro-regular-svg-icons";

interface PolicyCalloutFrameProps {
    title?: string;
    tagline?: string;
    subheading?: ReactNode;
    children?: ReactNode;
    bottomSlot?: ReactNode;
    showAccolades?: boolean;
    accoladesVariant?: "default" | "whySpot" | "coverage";
    accoladesPosition?: "above" | "below";
    showWhySpotBox?: boolean;
    whySpotBadgeText?: string;
    whySpotBadgeSuperscript?: string;
    containerClassName?: string;
    titleClassName?: string;
    contentClassName?: string;
}

export const PolicyCalloutFrame: React.FC<PolicyCalloutFrameProps> = ({
    title,
    tagline,
    subheading,
    children,
    bottomSlot,
    showAccolades = true,
    accoladesVariant = "default",
    accoladesPosition = "below",
    showWhySpotBox = false,
    whySpotBadgeText = "Why choose Spot?",
    whySpotBadgeSuperscript = "",
    containerClassName,
    titleClassName,
    contentClassName
}) => {
    const content = showWhySpotBox ? (
        <div className={cn("relative flex flex-col gap-2 rounded-xl border border-stroke-secondary-invert sm:gap-3 lg:gap-2 xl:gap-3", contentClassName)}>
            <Badge variant="brandB" className="absolute -top-3 left-1/2 w-fit -translate-x-1/2 px-3 py-1 leading-4">
                {whySpotBadgeText}
                {}
                {whySpotBadgeSuperscript && <sup className="top-[-2px] opacity-50">{whySpotBadgeSuperscript}</sup>}
            </Badge>
            {children}
        </div>
    ) : (
        <div className={contentClassName}>{children}</div>
    );

    const { appState } = useAppLayerContext();
    const { partnerOffer } = appState;

    return (
        <div className={cn("flex flex-col gap-6", containerClassName)}>
            {(title || tagline || subheading) && (
                <div className="space-y-1">
                    {partnerOffer && (
                        <div className="lg:flex lg:justify-center xl:justify-start">
                            <Badge variant="success" className="mb-2 gap-1 rounded-lg px-3 py-2">
                                <FontAwesomeIcon icon={faTag} className="size-4" />
                                <p className="text-sm font-medium leading-5">
                                    <strong>{partnerOffer.headline}</strong> {partnerOffer.offerText}
                                </p>
                            </Badge>
                        </div>
                    )}
                    {tagline && <div className="font-bold text-content-brand-b md:text-lg lg:text-center lg:text-base xl:text-start">{tagline}</div>}
                    {title && <h1 className={cn("text-2xl sm:text-3xl md:text-5xl md:font-extrabold lg:text-center", titleClassName)}>{title}</h1>}
                    {subheading && (
                        <p className="text-sm font-medium leading-5 text-content-primary sm:text-base sm:leading-6 md:text-xl md:leading-7 lg:text-sm lg:leading-5 xl:text-base xl:leading-6">
                            {subheading}
                        </p>
                    )}
                </div>
            )}

            {showAccolades && accoladesPosition === "above" && <Accolades variant={accoladesVariant} />}

            {children && content}

            {showAccolades &&
                accoladesPosition === "below" &&
                (bottomSlot ? (
                    <div className="order-2 xl:order-none">
                        <Accolades variant={accoladesVariant} />
                    </div>
                ) : (
                    <Accolades variant={accoladesVariant} />
                ))}

            {bottomSlot && <div className="order-1 xl:order-none">{bottomSlot}</div>}
        </div>
    );
};
